import { Alert, Button, Grid, Typography } from "@mui/material";
import React, { useMemo } from "react";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import BuchungenTable from "./BuchungenTable";
import BuchungenStatistics from "./BuchungenStatistics";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import {
  useDeleteBookingMutation,
  useGetBookingsQuery,
} from "../../../features/banking/bookingsApiSlice";
import { setDialog } from "../../../features/dialog/dialogSlice";

const TYPE_LABELS = {
  contribution: "Beiträge",
  testing: "Prüfungen",
  fee: "Gebühren",
  other: "Sonstiges",
};

const TYPE_ORDER = ["contribution", "testing", "fee", "other"];

const parseEuro = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return value;
  const normalized = String(value)
    .replace(/\s/g, "")
    .replace("€", "")
    .replace(/\./g, "")
    .replace(",", ".");
  const num = Number(normalized);
  return Number.isFinite(num) ? num : 0;
};

const getBookingType = (booking) => {
  const type = booking?.bookingType;
  if (TYPE_ORDER.includes(type)) return type;
  return booking?.bookMonth ? "contribution" : "other";
};

const getMonthKey = (booking) => {
  if (booking?.bookMonth) {
    const parts = String(booking.bookMonth).split("/");
    if (parts.length === 2) {
      const [mm, yyyy] = parts;
      if (mm && yyyy) return `${yyyy}-${mm.padStart(2, "0")}`;
    }
  }
  const date =
    booking?.bookingDate || booking?.date || booking?.createdAt || null;
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format("YYYY-MM") : "unknown";
};

const formatMonthLabel = (key) => {
  if (key === "unknown") return "Unbekannt";
  const parsed = dayjs(`${key}-01`);
  return parsed.isValid() ? parsed.format("MM/YYYY") : "Unbekannt";
};

export default function BankingStatistics() {
  const dispatch = useDispatch();
  const {
    data: bookings = [],
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetBookingsQuery();
  const [deleteBooking, { isLoading: isDeleting }] =
    useDeleteBookingMutation();

  const normalizedBookings = useMemo(
    () =>
      bookings.map((booking) => ({
        ...booking,
        bookingType: getBookingType(booking),
        bookingDate:
          booking.bookingDate || booking.date || booking.createdAt || null,
        membersCount:
          booking.membersCount ??
          booking.activeMembers ??
          (Number(booking.activeMembers) || 0),
        totalSum: parseEuro(booking.totalSum),
      })),
    [bookings]
  );

  const chartData = useMemo(() => {
    const map = new Map();
    normalizedBookings.forEach((booking) => {
      const key = getMonthKey(booking);
      const entry = map.get(key) || {
        key,
        label: formatMonthLabel(key),
        totals: {
          contribution: 0,
          testing: 0,
          fee: 0,
          other: 0,
        },
        members: 0,
      };
      const type = getBookingType(booking);
      entry.totals[type] += booking.totalSum || 0;
      if (type === "contribution") {
        entry.members += Number(booking.membersCount) || 0;
      }
      map.set(key, entry);
    });

    const sorted = Array.from(map.values()).sort((a, b) =>
      a.key.localeCompare(b.key)
    );

    const categories = sorted.map((entry) => entry.label);
    const series = TYPE_ORDER.map((type) => ({
      name: TYPE_LABELS[type],
      type: "column",
      data: sorted.map((entry) => Number(entry.totals[type].toFixed(2))),
    }));

    const membersSeries = {
      name: "Mitglieder",
      type: "line",
      data: sorted.map((entry) => entry.members),
    };

    return {
      categories,
      series: [...series, membersSeries],
    };
  }, [normalizedBookings]);

  const handleDeleteBooking = async (booking) => {
    if (!booking?._id) return;
    const ok = window.confirm(
      "Soll diese Buchung wirklich gelöscht werden? Dies kann nicht rückgängig gemacht werden."
    );
    if (!ok) return;
    try {
      await deleteBooking(booking._id).unwrap();
      dispatch(
        setDialog({
          type: "SUCCESS",
          title: "Buchung gelöscht",
          data: {
            successText: "Die Buchung wurde entfernt.",
          },
        })
      );
    } catch (err) {
      console.error("Delete booking failed:", err);
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Fehler",
          data: {
            errorText:
              err?.data?.message || "Die Buchung konnte nicht gelöscht werden.",
          },
        })
      );
    }
  };

  return (
    <div>
      {(isLoading || isFetching || isDeleting) && <LoadingScreen />}
      <div style={{ marginTop: "20px", marginBottom: "15px" }}>
        <Typography variant="h4">Banking Statistik</Typography>
      </div>
      <div>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Button variant="outlined" color="primary" onClick={refetch}>
              Aktualisieren
            </Button>
          </Grid>
          {isError && (
            <Grid item xs={12}>
              <Alert severity="error">
                {error?.data?.message ||
                  "Fehler beim Laden der Buchungen."}
              </Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <BuchungenStatistics
              chartLabels={chartData.categories}
              chartSeries={chartData.series}
            />
          </Grid>
          <Grid item xs={12}>
            <BuchungenTable
              buchungen={normalizedBookings}
              handleDeleteBuchung={handleDeleteBooking}
            />
          </Grid>
        </Grid>
      </div>
    </div>
  );
}
